// client/src/pages/Admin/ResourceManager/services/resourceService.ts
import { Resource, ResourceStatus } from '../../../../shared/models/Resource';
import { RESOURCE_FUNCTIONS } from '../constants/resource.constants';

export class ResourceService {
    private getToken(): string | null {
        return localStorage.getItem('adminToken');
    }

    private async handleResponse<T>(response: Response, fallbackMessage: string): Promise<T> {
        if (response.status === 401) {
            localStorage.removeItem('adminToken');
            window.location.href = '/admin';
            throw new Error('Session expired');
        }

        if (!response.ok) {
            try {
                const data = await response.json();
                throw new Error(data.error || fallbackMessage);
            } catch {
                throw new Error(fallbackMessage);
            }
        }

        return response.json();
    }

    async fetchResources(): Promise<Resource[]> {
        const token = this.getToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await fetch(RESOURCE_FUNCTIONS.list, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        const data = await this.handleResponse<{ data: Resource[] }>(response, 'Failed to fetch resources');
        return data.data || [];
    }

    async deleteResource(id: string): Promise<void> {
        const token = this.getToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await fetch(RESOURCE_FUNCTIONS.delete, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ id })
        });

        await this.handleResponse(response, 'Failed to delete resource');
    }

    async updateResource(id: string, updates: Partial<Resource>): Promise<void> {
        const token = this.getToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await fetch(RESOURCE_FUNCTIONS.update, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                id,
                ...updates,
                updatedAt: new Date()
            })
        });

        await this.handleResponse(response, 'Failed to update resource');
    }

    async bulkUpdateResources(ids: string[], updates: Partial<Resource>): Promise<void> {
        const token = this.getToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        const promises = ids.map(id => 
            fetch(RESOURCE_FUNCTIONS.update, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id,
                    ...updates,
                    updatedAt: new Date()
                })
            })
        );

        const responses = await Promise.all(promises);
        const failed = responses.filter(r => !r.ok);
        
        if (failed.length > 0) {
            throw new Error(`Failed to update ${failed.length} resource(s)`);
        }
    }

    async bulkDeleteResources(ids: string[]): Promise<void> {
        const token = this.getToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        const promises = ids.map(id => 
            fetch(RESOURCE_FUNCTIONS.delete, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id })
            })
        );

        const responses = await Promise.all(promises);
        const failed = responses.filter(r => !r.ok);
        
        if (failed.length > 0) {
            throw new Error(`Failed to delete ${failed.length} resource(s)`);
        }
    }
}

export const resourceService = new ResourceService();

export const bulkUpdateStatus = async (ids: string[], status: ResourceStatus): Promise<void> => {
    await resourceService.bulkUpdateResources(ids, { status });
};

export const bulkDeleteResources = async (ids: string[]): Promise<void> => {
    await resourceService.bulkDeleteResources(ids);
};

export const bulkToggleFeatured = async (ids: string[], featured: boolean): Promise<void> => {
    await resourceService.bulkUpdateResources(ids, { isFeatured: featured });
};